import { useEffect, useLayoutEffect, useRef } from "react"
import type { RefObject } from "react"
import type { CardId } from "@/types"
import styles from "@/styles/pages/Priorities.module.css"

/*
 * A card travelling from the focus slot into the quadrant it was filed in.
 *
 * The board is one card at a time and the ROOM does not touch it — the
 * facilitator files, everyone else watches, which is why the participant view is
 * the same board read-only (see `Priorities`). But watching only works if there
 * is something to watch: a placement arrives as a Firestore snapshot, so without
 * this the card vanished from the focus slot and a 56px tile appeared in a
 * quadrant in the same frame. Nobody looking at the board saw WHICH quadrant it
 * went to, which is the one thing this round decides.
 *
 * So the arrival is replayed as the gesture that produced it: the card lifts off
 * the slot, arcs across and shrinks into its place. It runs off the snapshot
 * rather than off the click, so it is identical for the facilitator who dropped
 * the card, for a guest on a phone, and for a second facilitator watching —
 * nobody has to be the one who moved it.
 *
 * Entirely outside React, and deliberately so. The flying card is a CLONE of the
 * card that was on screen, parked on `body` in fixed coordinates: it has to
 * cross the board's quadrants and whatever scrolls between them, it must be the
 * card the room was just reading — same family ground, same side up — and
 * nothing about the round depends on it. Rendering it would mean state set from
 * a layout effect, a cascading render per placement, and a second definition of
 * a card that could drift from the first.
 */

/* Kept in step with the cardLand animation in Priorities.module.css: the tile
   underneath is held down for exactly this long, so a ghost that outlived it
   would land on a card already sitting there. */
const FLIGHT_MS = 520
/* When the tile comes back up, as a fraction of the flight. It rises UNDER the
   card while the card fades out on top of it — a straight swap at the end trades
   a drawn card for a coloured tile in one frame, which reads as a flicker
   exactly where the eye is. */
const TILE_HOLD = 0.66

interface Landing {
  /** Every card the board is holding, in any quadrant. */
  placed: readonly CardId[]
  /** The card in the focus slot, which is the one a flight can start from. */
  focusId: CardId | null
  /** Wraps the focus card; the card itself is its only child. */
  focusRef: RefObject<HTMLElement | null>
  /** The four quadrants. A placed card marks its slot `data-landing="<cardId>"`. */
  boardRef: RefObject<HTMLElement | null>
}

/** Watches the set of filed cards and animates each new arrival. */
export function useCardLanding({ placed, focusId, focusRef, boardRef }: Landing) {
  /*
   * The card that was in the focus slot last pass, already cloned, with the box
   * it occupied.
   *
   * Cloned EAGERLY because by the time a placement arrives the slot has advanced
   * to the next card — the snapshot that files one is the snapshot that brings up
   * the other, so there is nothing left to copy. The card id is kept with it so a
   * flight can check it is animating the card that was actually on screen: the
   * facilitator can pull a card back out of the board and re-file it, and on
   * everyone else's screen the slot is still showing the top of the queue.
   */
  const previous = useRef<{ cardId: CardId; node: HTMLElement; box: DOMRect } | null>(null)
  /** null until the first pass: a round opened half-filed must not deal itself in. */
  const seen = useRef<Set<CardId> | null>(null)
  const inFlight = useRef(new Set<HTMLElement>())

  // A card still in the air when the round moves on has nowhere to land, and it
  // is parked on `body`, so nothing else would ever take it off.
  useEffect(
    () => () => {
      for (const ghost of inFlight.current) ghost.remove()
      inFlight.current.clear()
    },
    []
  )

  /*
   * No dependency list on purpose: the set of filed cards is a fresh array every
   * render, so a list would either be a lie or would have to be a joined key, and
   * this is two `getBoundingClientRect`s and a small clone against a handful of
   * ids. Nothing here sets state, so it cannot cascade.
   */
  useLayoutEffect(() => {
    const before = seen.current
    seen.current = new Set(placed)
    const from = previous.current

    /* Under reduced motion the card is simply in the quadrant it was filed in,
       which is the same information without the travel. */
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false

    if (before && from && !still) {
      for (const cardId of placed) {
        if (before.has(cardId) || cardId !== from.cardId) continue

        const slot = boardRef.current?.querySelector<HTMLElement>(`[data-landing="${cardId}"]`)
        const to = slot?.getBoundingClientRect()
        if (!slot || !to || to.width === 0 || from.box.width === 0) continue

        fly(from.node, from.box, to, inFlight.current)

        /*
         * The tile it turns into, held at nothing under the card and brought up
         * as the card fades out on top of it. They overlap on purpose: swapping
         * a drawn card for a coloured tile in one frame reads as a flicker
         * exactly where the eye is.
         *
         * Animated rather than given a class, because the element belongs to
         * React and its `className` is a prop — this is the one way to move it
         * without writing to something React owns.
         */
        slot.animate([{ opacity: 0 }, { opacity: 0, offset: TILE_HOLD }, { opacity: 1 }], {
          duration: FLIGHT_MS,
          easing: "linear",
        })
      }
    }

    /*
     * The card, not the slot around it: the slot is a centred flex box the width
     * of the column, so its box would start the flight left of the card and scale
     * it against the wrong width. `CardFace` renders a single root, and its width
     * is a `clamp()` on vw — measured, never assumed.
     */
    const card = focusRef.current?.firstElementChild as HTMLElement | null | undefined
    const box = card?.getBoundingClientRect()
    /* Left standing when the slot empties — the last card of the round is filed
       into a board whose focus slot has become the "all placed" panel, and its
       flight still has to start where the card was. */
    if (focusId && card && box && box.width > 0) {
      previous.current = { cardId: focusId, node: card.cloneNode(true) as HTMLElement, box }
    }
  })
}

/**
 * Parks a copy of the card at the box it is leaving and lets CSS carry it to the
 * box it is going to. The delta and the shrink are handed over as custom
 * properties; the path itself is in `Priorities.module.css`, with the rest of
 * this screen's motion.
 */
function fly(node: HTMLElement, from: DOMRect, to: DOMRect, tracked: Set<HTMLElement>) {
  /*
   * The flight goes on a wrapper of my own rather than on the card, and the card
   * inside it is pinned still.
   *
   * Both are about the same thing: `CardFace` animates its own `transform` when a
   * card is turned over, from a rule specific enough to win the shorthand
   * outright, so a flight set on the card would simply not run — and even where
   * it did, a card cannot compose two transforms on one element. The clone is a
   * STILL of what was on screen: whatever side was showing, not a card turning
   * over again on its way to the board.
   */
  const ghost = document.createElement("div")
  ghost.className = styles.flight
  ghost.setAttribute("aria-hidden", "true")
  ghost.inert = true
  ghost.style.left = `${from.left}px`
  ghost.style.top = `${from.top}px`
  ghost.style.setProperty("--fly-x", `${to.left - from.left}px`)
  ghost.style.setProperty("--fly-y", `${to.top - from.top}px`)
  ghost.style.setProperty("--fly-s", `${to.width / from.width}`)

  /* Cloned again rather than adopted: what is handed in is the snapshot of the
     focus slot, and the next pass reads it as the origin of the next flight. A
     node cannot be in two places. */
  const card = node.cloneNode(true) as HTMLElement
  card.style.animation = "none"
  ghost.appendChild(card)

  const done = () => {
    ghost.remove()
    tracked.delete(ghost)
  }
  // The flight's own end, not one bubbling up from anything the card animates
  // inside itself.
  ghost.addEventListener("animationend", (event) => {
    if (event.target === ghost) done()
  })
  // The animation not running at all — a hidden tab, a stylesheet that has not
  // arrived — must not leave a card lying on the page.
  window.setTimeout(done, FLIGHT_MS + 400)

  tracked.add(ghost)
  document.body.appendChild(ghost)
}
